import { Dialog, Button, Grid, FormControl, RadioGroup, FormControlLabel, Radio, Typography } from "@mui/material";
import { isEqual } from "lodash";
import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Container, Content, ContentText, RadioGroupContainer } from "./styles";
import DialogActions from "../../../../containers/DialogActions/DialogActions";
import DialogTitle from "../../../../containers/DialogTitle/DialogTitle";
import LoadingOverlay from "../../../../containers/LoadingOverlay/LoadingOverlay";
import { CompanyLegalEntityDTO } from "../../../../models/company-accounts/company-legal-entities";
import { EmployeeGroupDTO } from "../../../../models/employee-groups";
import { EmployeeRef } from "../../../../models/employees";
import { ExpenseReportDTO, ExpenseReportFilter } from "../../../../models/expense-reports";
import { EmployeeGroupsApiService } from "../../../../services/employee-groups/employee-groups-api.service";
import { EXPENSE_REPORT_STATUS } from "../../../../utils/constants/constants";
import {
  CONTENT_GAP,
  exportExpenseReportsToPDF,
  HEADER_FOR_PDF,
} from "../../../../utils/export-expense-reports-utils/export-expense-reports-as-pdf";
import { exportTablesToExcel } from "../../../../utils/export-tables-to-excel";
import { intlFormatDateShort } from "../../../../utils/time-utils";
import ExpenseReportTable from "../ExpenseReportTable/ExpenseReportTable";
import { ExpenseReportView, fetchFlagImagesAsBase64, uniqueCountyCodes } from "../utils";

enum Options {
  excel = "Excel",
  pdf = "PDF",
}

const optionsToExport = [Options.excel, Options.pdf];

interface Props {
  disabled: boolean;
  items: ExpenseReportDTO[];
  propsFilter: ExpenseReportFilter;
  view: ExpenseReportView;
  selectedLegalEntity?: CompanyLegalEntityDTO | null;
  employee?: EmployeeRef;
}

interface State {
  open: boolean;
  loading: boolean;
  selectedOption: Options;
  error: string;
}

const ExportExpenseReport = ({ disabled, items, propsFilter, view, selectedLegalEntity, employee }: Props) => {
  const { t } = useTranslation();
  const tRP = "pages.my_travel_and_expenses.tables.expense_reports";
  const tRPCommon = "pages.company_account_profile.tables.employees.fields";

  const [state, setState] = useState<State>({
    open: false,
    loading: false,
    selectedOption: Options.excel,
    error: "",
  });

  const handleClose = () => {
    setState((state) => ({
      ...state,
      open: false,
      selectedOption: Options.excel,
      loading: false,
      error: "",
    }));
  };

  const loadEmployeeGroup = useCallback(async () => {
    const response = await new EmployeeGroupsApiService().loadItems({
      ids: propsFilter?.employeeGroupIds,
    });

    if (!response.error) {
      return response.items[0];
    } else {
      setState((state) => ({
        ...state,
        error: response.error,
      }));
    }
  }, [propsFilter?.employeeGroupIds]);

  const rowsBeforeHeaderInExcel = useCallback(
    (employeeGroup?: EmployeeGroupDTO) => {
      return view === ExpenseReportView.manager
        ? [
            {
              data: [
                `Legal entities: ${!propsFilter?.legalEntityIds?.length ? "All" : selectedLegalEntity?.name ?? ""}`,
              ],
              fontSize: 16,
              isBold: true,
              emptyRowsUnder: 1,
            },
            {
              data: [
                `Employee group: ${
                  !propsFilter?.employeeGroupIds?.length ? "All groups" : `${employeeGroup?.name ?? ""}`
                }`,
              ],
              fontSize: 12,
              isBold: false,
            },
            {
              data: [`Period: ${propsFilter.year} year`],
              fontSize: 14,
              isBold: true,
            },
          ]
        : [
            {
              data: [`Employee: ${employee?.name ?? ""}`],
              fontSize: 16,
              isBold: true,
              emptyRowsUnder: 1,
            },
            {
              data: [`Legal entity: ${employee?.legalEntity?.name ?? ""}`],
              fontSize: 12,
              isBold: false,
            },
            {
              data: [`Employee code: ${employee?.employeeCode ?? ""}`],
              fontSize: 12,
              isBold: false,
              emptyRowsUnder: 1,
            },
            {
              data: [`Period: ${propsFilter.year} year`],
              fontSize: 14,
              isBold: true,
            },
          ];
    },
    [view, propsFilter, selectedLegalEntity, employee]
  );

  const headerNames = useMemo(() => {
    return [
      `${t(`${tRP}.columns.date`)}`,
      `${t(`${tRPCommon}.employee_code`)}`,
      `${t(`${tRPCommon}.internal_id`)}`,
      `${t("common_words.employee")}`,
      `${t(`${tRPCommon}.birth_date`)}`,
      `${t("common_words.legal_entity")}`,
      `${t(`${tRP}.columns.status`)}`,
      `${t(`${tRP}.columns.description`)}`,
      `${t(`${tRP}.columns.total`)}`,
      `${t(`${tRP}.columns.currency`)}`,
      `${t(`${tRP}.columns.client_approval`)}`,
      `${t(`${tRP}.columns.documentation_audit`)}`,
      `${t(`${tRP}.columns.approval`)}`,
      `${t(`${tRP}.columns.payment`)}`,
    ];
  }, []);

  const dataToExportAsExcel = useCallback(
    (allItems: ExpenseReportDTO[]) => {
      return allItems.map((item) => {
        return {
          [`${t(`${tRP}.columns.date`)}`]: { value: item.date ? intlFormatDateShort(item.date) : " " },
          [`${t(`${tRPCommon}.employee_code`)}`]: { value: item.employee?.employeeCode ?? " " },
          [`${t(`${tRPCommon}.internal_id`)}`]: { value: item.employee?.internalId ?? " " },
          [`${t("common_words.employee")}`]: { value: item.employee?.name ?? " " },
          [`${t(`${tRPCommon}.birth_date`)}`]: {
            value: item.employee?.birthDate ? intlFormatDateShort(item.employee?.birthDate) : " ",
          },
          [`${t("common_words.legal_entity")}`]: { value: item?.legalEntity?.name ?? " " },
          [`${t(`${tRP}.columns.status`)}`]: { value: item?.status?.name ?? " " },
          [`${t(`${tRP}.columns.description`)}`]: { value: item?.description ?? " " },
          [`${t(`${tRP}.columns.total`)}`]: {
            value: item?.totalAmount && item?.currency?.code ? `${item.totalAmount.toLocaleCustom()} ` : " ",
          },
          [`${t(`${tRP}.columns.currency`)}`]: { value: item?.currency?.code ?? "" },
          [`${t(`${tRP}.columns.client_approval`)}`]: {
            value: item?.needAcceptance
              ? item?.acceptedByClient
                ? "Accepted"
                : item?.rejectedByClient
                ? "Rejected"
                : " "
              : " ",
          },
          [`${t(`${tRP}.columns.documentation_audit`)}`]: { value: item?.documentation ? "Yes" : "No" },
          [`${t(`${tRP}.columns.approval`)}`]: {
            value:
              isEqual(item.status?.id, EXPENSE_REPORT_STATUS.approved.id) ||
              isEqual(item.status?.id, EXPENSE_REPORT_STATUS.paid.id)
                ? "Yes"
                : "No",
          },
          [`${t(`${tRP}.columns.payment`)}`]: {
            value: isEqual(item?.status?.id, EXPENSE_REPORT_STATUS.paid.id) ? "Yes" : "No",
          },
        };
      });
    },
    [headerNames]
  );

  const exportToExcel = useCallback(async () => {
    let eeGroupsRes;

    if (view === ExpenseReportView.manager && !!propsFilter?.employeeGroupIds?.length) {
      const eeGroup = await loadEmployeeGroup();

      if (!eeGroup) return;

      eeGroupsRes = eeGroup;
    }

    await exportTablesToExcel(
      dataToExportAsExcel(items),
      headerNames as string[],
      "ExpenseReport",
      rowsBeforeHeaderInExcel(eeGroupsRes)
    );
  }, [rowsBeforeHeaderInExcel, headerNames, dataToExportAsExcel, view, propsFilter, loadEmployeeGroup, items]);

  const exportToPDF = useCallback(async () => {
    let eeGroupsRes;

    if (view === ExpenseReportView.manager && !!propsFilter?.employeeGroupIds?.length) {
      const eeGroup = await loadEmployeeGroup();

      if (!eeGroup) return;

      eeGroupsRes = eeGroup;
    }

    const allCountryCodes = items
      .map((report) => {
        return report.travelExpenses
          .map((receipt) => {
            return receipt.country?.code;
          })
          .filter((code) => !!code);
      })
      .reduce((acc, curr) => [...acc, ...curr], []) as string[];

    const preloadedFlags = await fetchFlagImagesAsBase64(uniqueCountyCodes(allCountryCodes));

    const contentToExport = (employeeGroup?: EmployeeGroupDTO) => {
      return (
        <Grid sx={{ display: "flex", flexDirection: "column", gap: `${CONTENT_GAP}px` }}>
          <Grid
            sx={{ display: "flex", flexDirection: "column", gap: "10px" }}
            className={HEADER_FOR_PDF}
          >
            {view === ExpenseReportView.employee && (
              <>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: "bold" }}
                >
                  {`Employee: ${employee?.name ?? ""}`}
                </Typography>
                <Typography variant="h5">{`Legal entity: ${employee?.legalEntity?.name ?? ""}`}</Typography>
                <Typography variant="h5">{`Employee code: ${employee?.employeeCode ?? ""}`}</Typography>

                <Typography
                  variant="h4"
                  sx={{ fontWeight: "bold" }}
                >{`Period: ${propsFilter.year} year`}</Typography>
              </>
            )}

            {view === ExpenseReportView.manager && (
              <>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: "bold" }}
                >
                  {`Legal entities: ${!propsFilter?.legalEntityIds?.length ? "All" : selectedLegalEntity?.name ?? ""}`}
                </Typography>
                <Typography variant="h5">{`Employee group: ${
                  !propsFilter?.employeeGroupIds?.length ? "All groups" : `${employeeGroup?.name ?? ""}`
                }`}</Typography>

                <Typography
                  variant="h4"
                  sx={{ fontWeight: "bold" }}
                >{`Period: ${propsFilter.year} year`}</Typography>
              </>
            )}
          </Grid>

          <ExpenseReportTable
            view={view}
            items={items}
            handleNotifyParent={async () => {}}
            forExport
            preloadedFlags={preloadedFlags}
          />
        </Grid>
      );
    };

    await exportExpenseReportsToPDF(
      contentToExport(eeGroupsRes),
      () => {
        setState((state) => ({
          ...state,
          error: "Error generateing PDF file",
        }));

        return;
      },
      `${view === ExpenseReportView.manager ? "My_team_expense_report" : "My_expense_report"}`
    );
  }, [view, propsFilter, loadEmployeeGroup, items]);

  const handleExport = useCallback(async () => {
    setState((state) => ({
      ...state,
      loading: true,
    }));

    try {
      if (state.selectedOption === Options.excel) {
        await exportToExcel();
      } else if (state.selectedOption === Options.pdf) {
        await exportToPDF();
      }

      handleClose();
    } catch (error) {
      console.log("Error: ", error);

      setState((state) => ({
        ...state,
        error: `Error generating ${
          state.selectedOption === Options.excel ? "excel" : state.selectedOption === Options.pdf ? "PDF" : ""
        } file`,
      }));
    } finally {
      setState((state) => ({
        ...state,
        loading: false,
      }));
    }
  }, [exportToExcel, state.selectedOption, exportToPDF]);

  return (
    <>
      <Button
        title={`${t("common_words.export")}`}
        variant="contained"
        onClick={() => {
          setState((state) => ({
            ...state,
            open: true,
          }));
        }}
        sx={{ textWrap: "nowrap", textTransform: "none", padding: "5px", height: "25px" }}
        disabled={disabled}
      >
        {`${t("common_words.export")}`}
      </Button>

      <Dialog
        open={state.open}
        onClose={handleClose}
      >
        <LoadingOverlay loading={state.loading}>
          <DialogTitle
            title={"Export expense report"}
            handleClose={handleClose}
          />
          <Content>
            <Container>
              <Grid sx={{ padding: "5px" }}>
                <ContentText>Select how to export:</ContentText>
              </Grid>

              <RadioGroupContainer>
                <FormControl>
                  <RadioGroup
                    aria-labelledby="radio-group"
                    defaultValue={optionsToExport[0]}
                    name="radio-buttons-group"
                    onChange={(event, value) => {
                      setState((state) => ({
                        ...state,
                        selectedOption: value as Options,
                      }));
                    }}
                  >
                    {optionsToExport.map((option) => {
                      return (
                        <FormControlLabel
                          key={option}
                          value={option}
                          control={<Radio />}
                          label={option}
                        />
                      );
                    })}
                  </RadioGroup>
                </FormControl>
              </RadioGroupContainer>
            </Container>
          </Content>

          <DialogActions
            handleCancel={handleClose}
            useGoback
            error={state.error}
          >
            <Button
              onClick={handleExport}
              variant="contained"
            >
              {`${t("buttons.confirm")}`}
            </Button>
          </DialogActions>
        </LoadingOverlay>
      </Dialog>
    </>
  );
};

export default ExportExpenseReport;
